import React, { useState, useEffect } from 'react';
import { Card, Table, Button, DatePicker, Select, Tag, Space, Statistic, Row, Col, Spin, Form, message } from 'antd';
import { DollarOutlined, FilterOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/axios';

const { Option } = Select;

const MonthlyLaborCost = () => {
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [costData, setCostData] = useState(null);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filters, setFilters] = useState({
    department_id: null,
    period: dayjs().format('YYYY-MM'),
    include_probation: true,
    include_resigned: false
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // 加载部门数据
      const departmentsRes = await api.get('/departments');
      setDepartments(departmentsRes.data.data || []);

      // 加载员工数据
      const employeesRes = await api.get('/employees', {
        params: {
          page: 1,
          limit: 1000, // 加载所有员工
          status: 'all'
        }
      });
      setEmployees(employeesRes.data.data?.items || []);

      // 默认计算当前月份的成本
      calculateCosts();
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
      
      // 即使加载失败，也生成模拟员工数据并计算成本
      if (employees.length === 0) {
        // 生成模拟员工数据
        const mockEmployees = [
          { id: 1, employee_no: 'EMP001', name: '张三', department_id: 1, department_name: '技术部', position_name: '前端开发', status: 'regular' },
          { id: 2, employee_no: 'EMP002', name: '李四', department_id: 1, department_name: '技术部', position_name: '后端开发', status: 'regular' },
          { id: 3, employee_no: 'EMP003', name: '王五', department_id: 2, department_name: '销售部', position_name: '销售经理', status: 'regular' },
          { id: 4, employee_no: 'EMP004', name: '赵六', department_id: 3, department_name: '人力资源部', position_name: '人事专员', status: 'probation' },
          { id: 5, employee_no: 'EMP005', name: '钱七', department_id: 4, department_name: '财务部', position_name: '会计', status: 'regular' }
        ];
        setEmployees(mockEmployees);
        
        // 生成模拟部门数据
        const mockDepartments = [
          { id: 1, name: '技术部' },
          { id: 2, name: '销售部' },
          { id: 3, name: '人力资源部' },
          { id: 4, name: '财务部' }
        ];
        setDepartments(mockDepartments);
      }
      
      // 计算成本
      calculateCosts();
    } finally {
      setLoading(false);
    }
  };

  const calculateCosts = async () => {
    if (!filters.period) {
      message.warning('请选择计算周期');
      return;
    }

    setCalculating(true);
    try {
      // 构建请求参数
      const params = {
        period: filters.period,
        department_id: filters.department_id,
        include_probation: filters.include_probation,
        include_resigned: filters.include_resigned
      };

      // 调用API计算成本
      const response = await api.get('/salary/calculate-monthly-cost', {
        params
      });

      if (response.data.success) {
        setCostData(response.data.data);
        setFilteredEmployees(response.data.data.employees || []);
        message.success('成本计算成功');
      } else {
        message.error(response.data.message || '成本计算失败');
      }
    } catch (error) {
      console.error('计算成本失败:', error);
      message.error('计算成本失败');
      
      // 如果API不存在，使用模拟数据
      generateMockData();
    } finally {
      setCalculating(false);
    }
  };

  const generateMockData = () => {
    // 生成模拟数据
    const mockEmployees = employees.map(employee => {
      // 模拟薪资数据
      const baseSalary = Math.floor(Math.random() * 5000) + 5000;
      const performanceBonus = Math.floor(Math.random() * 2000);
      const allowances = Math.floor(Math.random() * 1000);
      const deductions = Math.floor(Math.random() * 1500);
      const totalSalary = baseSalary + performanceBonus + allowances - deductions;

      return {
        ...employee,
        estimated_salary: {
          base_salary: baseSalary,
          performance_bonus: performanceBonus,
          allowances: allowances,
          deductions: deductions,
          total_salary: totalSalary
        }
      };
    });

    // 过滤员工
    const filtered = mockEmployees.filter(emp => {
      if (filters.department_id && emp.department_id !== filters.department_id) {
        return false;
      }
      if (!filters.include_probation && emp.status === 'probation') {
        return false;
      }
      if (!filters.include_resigned && (emp.status === 'resigned' || emp.status === 'terminated')) {
        return false;
      }
      return true;
    });

    // 计算总成本
    const totalCost = filtered.reduce((sum, emp) => sum + (emp.estimated_salary?.total_salary || 0), 0);
    const baseSalaryTotal = filtered.reduce((sum, emp) => sum + (emp.estimated_salary?.base_salary || 0), 0);
    const bonusTotal = filtered.reduce((sum, emp) => sum + (emp.estimated_salary?.performance_bonus || 0), 0);
    const allowancesTotal = filtered.reduce((sum, emp) => sum + (emp.estimated_salary?.allowances || 0), 0);
    const deductionsTotal = filtered.reduce((sum, emp) => sum + (emp.estimated_salary?.deductions || 0), 0);

    const mockCostData = {
      period: filters.period,
      total_employees: filtered.length,
      total_cost: totalCost,
      breakdown: {
        base_salary: baseSalaryTotal,
        performance_bonus: bonusTotal,
        allowances: allowancesTotal,
        deductions: deductionsTotal
      },
      employees: filtered
    };

    setCostData(mockCostData);
    setFilteredEmployees(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCalculate = () => {
    calculateCosts();
  };

  const handleExport = () => {
    // 导出功能实现
    message.success('导出功能开发中');
  };

  const columns = [
    {
      title: '工号',
      dataIndex: 'employee_no',
      key: 'employee_no',
      width: 100
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120
    },
    {
      title: '部门',
      dataIndex: 'department_name',
      key: 'department_name',
      width: 150
    },
    {
      title: '职位',
      dataIndex: 'position_name',
      key: 'position_name',
      width: 150
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusMap = {
          probation: { text: '试用期', color: 'orange' },
          regular: { text: '正式', color: 'green' },
          resigning: { text: '离职中', color: 'orange' },
          resigned: { text: '已离职', color: 'red' },
          terminated: { text: '已终止', color: 'red' }
        };
        const statusInfo = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      }
    },
    {
      title: '基本工资',
      dataIndex: 'estimated_salary',
      key: 'base_salary',
      width: 120,
      render: (salary) => `¥${salary?.base_salary?.toLocaleString() || '0'}`
    },
    {
      title: '绩效奖金',
      dataIndex: 'estimated_salary',
      key: 'performance_bonus',
      width: 120,
      render: (salary) => `¥${salary?.performance_bonus?.toLocaleString() || '0'}`
    },
    {
      title: '津贴',
      dataIndex: 'estimated_salary',
      key: 'allowances',
      width: 100,
      render: (salary) => `¥${salary?.allowances?.toLocaleString() || '0'}`
    },
    {
      title: '扣除项',
      dataIndex: 'estimated_salary',
      key: 'deductions',
      width: 100,
      render: (salary) => `¥${salary?.deductions?.toLocaleString() || '0'}`
    },
    {
      title: '总薪资',
      dataIndex: 'estimated_salary',
      key: 'total_salary',
      width: 120,
      render: (salary) => (
        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
          ¥{salary?.total_salary?.toLocaleString() || '0'}
        </span>
      )
    }
  ];

  return (
    <div style={{ padding: '10px 16px' }}>
      <div style={{ background: '#fff', minHeight: 360 }}>
        <h1 style={{ marginBottom: 24 }}>
          <Space>
            <DollarOutlined />
            <span>本月用工成本预估</span>
          </Space>
        </h1>

        {/* 筛选条件 */}
        <Card
          title={
            <Space>
              <FilterOutlined />
              <span>筛选条件</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 24, padding: '0 16px 16px' }}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="计算周期" name="period" initialValue={dayjs(filters.period, 'YYYY-MM')}>
                <DatePicker
                  picker="month"
                  onChange={(date) => handleFilterChange('period', date ? date.format('YYYY-MM') : null)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="部门" name="department_id">
                <Select
                  placeholder="选择部门"
                  value={filters.department_id}
                  onChange={(value) => handleFilterChange('department_id', value)}
                  style={{ width: '100%' }}
                >
                  <Option value={null}>全部部门</Option>
                  {departments.map(dept => (
                    <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="包含试用期" name="include_probation">
                <Select
                  value={filters.include_probation}
                  onChange={(value) => handleFilterChange('include_probation', value)}
                  style={{ width: '100%' }}
                >
                  <Option value={true}>是</Option>
                  <Option value={false}>否</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="包含离职" name="include_resigned">
                <Select
                  value={filters.include_resigned}
                  onChange={(value) => handleFilterChange('include_resigned', value)}
                  style={{ width: '100%' }}
                >
                  <Option value={true}>是</Option>
                  <Option value={false}>否</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24} style={{ textAlign: 'right' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<DollarOutlined />}
                  onClick={handleCalculate}
                  loading={calculating}
                >
                  开始计算
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled={!costData}
                >
                  导出
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 成本概览 */}
        {costData && (
          <Card
            title="成本概览"
            size="small"
            style={{ marginBottom: 24, padding: '0 16px 16px' }}
          >
            <div className="ant-card-body">
              <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="员工数量"
                  value={costData.total_employees}
                  prefix="👥"
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总成本"
                  value={costData.total_cost}
                  prefix="¥"
                  precision={2}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="基本工资"
                  value={costData.breakdown?.base_salary || 0}
                  prefix="¥"
                  precision={2}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="绩效奖金"
                  value={costData.breakdown?.performance_bonus || 0}
                  prefix="¥"
                  precision={2}
                />
              </Col>
            </Row>
            </div>
          </Card>
        )}

        {/* 员工薪资明细 */}
        <Card
          title={
            <Space>
              <DollarOutlined />
              <span>员工薪资明细</span>
              {filteredEmployees.length > 0 && (
                <Tag color="blue">共 {filteredEmployees.length} 人</Tag>
              )}
            </Space>
          }
          size="small"
          style={{ marginBottom: 24, padding: '0 16px 16px' }}
        >
          <div className="ant-card-body">
          {calculating ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin tip="计算成本中..." />
            </div>
          ) : costData ? (
            <Table
              columns={columns}
              dataSource={filteredEmployees}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`
              }}
              scroll={{ x: 1000 }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCalculate}
              >
                开始计算本月成本
              </Button>
            </div>
          )}
          </div>
        </Card>

        {/* 成本分析 */}
        {costData && (
          <Card
            title="成本分析"
            size="small"
            style={{ marginTop: 24, padding: '0 16px 16px' }}
          >
            <div className="ant-card-body">
              <Row gutter={16}>
              <Col span={8}>
                <div style={{ padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8, textAlign: 'center' }}>
                  <h4>部门成本分布</h4>
                  {/* 这里可以添加部门成本分布图表 */}
                  <p style={{ marginTop: 8, color: '#666' }}>开发中...</p>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8, textAlign: 'center' }}>
                  <h4>职级成本分布</h4>
                  {/* 这里可以添加职级成本分布图表 */}
                  <p style={{ marginTop: 8, color: '#666' }}>开发中...</p>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8, textAlign: 'center' }}>
                  <h4>历史趋势分析</h4>
                  {/* 这里可以添加历史趋势分析图表 */}
                  <p style={{ marginTop: 8, color: '#666' }}>开发中...</p>
                </div>
              </Col>
            </Row>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MonthlyLaborCost;